import { createAsyncThunk } from "@reduxjs/toolkit";
import type { AxiosError, CancelTokenSource } from "axios";
import axios from "axios";
import type { FetchMaintenanceHistoryPayload, FreshdeskConversation, FreshdeskConversationPayload, MaintenanceHistory, MaintRegisrty } from "../types/dashboardTypes";
import { axiosInstance } from "../../utils/common";

let cancelTokenSource14: CancelTokenSource | null = null;

export const fetchMaintRegistry = createAsyncThunk<
    MaintRegisrty[], // Return type
    FetchMaintenanceHistoryPayload, // Argument type
    { rejectValue: string }  // Optional reject value
>(
    "maintenance/fetchHistory",
    async (payload, thunkAPI) => {
        if (cancelTokenSource14) {
            cancelTokenSource14.cancel('Request canceled due to new request');
        }

        cancelTokenSource14 = axios.CancelToken.source();
        try {
            // Construct the URL dynamically
            const url = `maintenance/maint_register/`;

            const response = await axiosInstance.get<MaintRegisrty[]>(url, {
                headers: {
                    customer: payload.requestedSchema,
                },
                cancelToken: cancelTokenSource14.token,
            });

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                axios.isAxiosError(error) ? error.message : "An unknown error occurred"
            );
        }
    }
);

let cancelTokenSource13: CancelTokenSource | null = null;

export const fetchMaintenanceTickets = createAsyncThunk<
    MaintenanceHistory[], // Return type
    FetchMaintenanceHistoryPayload, // Argument type
    { rejectValue: string }  // Optional reject value
>(
    "maintenance/fetchMaintenanceTickets",
    async (payload, thunkAPI) => {
        // If there's already an ongoing request, cancel it
        if (cancelTokenSource13) {
            cancelTokenSource13.cancel('Request canceled due to new request');
        }

        cancelTokenSource13 = axios.CancelToken.source();

        try {
            const url =
                payload.selectedSite
                    ? `maintenance/ticket/${payload.selectedSite}/` :
                    `maintenance/ticket/`;

            const response = await axiosInstance.get<MaintenanceHistory[]>(url, {
                headers: {
                    customer: payload.requestedSchema,
                },
                cancelToken: cancelTokenSource13.token,
            });

            return response.data;
        } catch (error) {
            if (axios.isCancel(error)) {
                return thunkAPI.rejectWithValue('Request canceled');
            }
            return thunkAPI.rejectWithValue((error as AxiosError).message);
        }
    }
);

export const fetchMaintenanceTicketConvo = createAsyncThunk<
    FreshdeskConversation[], // Return type: array of FreshdeskConversation
    FreshdeskConversationPayload, // Argument type
    { rejectValue: string } // Optional reject value
>(
    "maintenance/fetchMaintenanceTicketConvo",
    async (payload, thunkAPI) => {
        try {
            const url = `maintenance/get_conversations/`;

            const response = await axiosInstance.get<FreshdeskConversation[]>(url, {
                headers: {
                    customer: payload.requestedSchema,
                },
            });

            return response.data;
        } catch (error) {
            let errorMessage = "An unknown error occurred";
            if (axios.isAxiosError(error)) {
                errorMessage = error.response?.data?.message || error.message;
            }
            return thunkAPI.rejectWithValue(errorMessage);
        }
    }
);

export interface FetchMaintenanceThresholdPayload {
    site?: number;
    selectedCustomerName: string;
}

export interface Site {
    id: number;
    name: string;
}

export interface Equipment {
    id: number;
    name: string;
}

export interface MaintenanceThresholdRecord {
    id: number;
    site: Site;
    current_engine_hours: number;
    equipment: Equipment;
    last_maintenance_hours: number;
    maintenance_threshold_hours: number;
    alert_threshold: number;
}

export interface MaintenanceThreshold2 {
    customer: string;
    data: MaintenanceThresholdRecord[];
}

// Async thunk action
export const fetchMaintenanceThreshold = createAsyncThunk<
    MaintenanceThresholdRecord[],  // Return type
    FetchMaintenanceThresholdPayload, // Payload type
    { rejectValue: string } // Reject type
>(
    "maintenance/fetchThreshold",
    async (payload, thunkAPI) => {
        try {
            const { site, selectedCustomerName } = payload;
            const url = site ? `maintenance/threshold/${site}/` : `maintenance/threshold/`;

            const response = await axiosInstance.get<MaintenanceThresholdRecord[]>(url, {
                headers: {
                    Accept: "application/json",
                    customer: selectedCustomerName,
                },
            });

            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                axios.isAxiosError(error) ? error.message : "An unknown error occurred"
            );
        }
    }
);
